import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    CheckCircle as CompleteIcon,
    Send as GenerateGRNIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface GradingItem {
    id: number;
    item_id: number;
    item_name: string;
    source_qty: number;
    grade_a_qty: number;
    grade_b_qty: number;
    grade_c_qty: number;
    qc_wastage_qty: number;
    total_graded: number;
}

interface GradingDetails {
    id: number;
    receiving_number: string;
    batch_number: string;
    po_number: string;
    vendor_name: string;
    grading_date: string;
    status: string;
    items: GradingItem[];
}

const GradingDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [grading, setGrading] = useState<GradingDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editedItems, setEditedItems] = useState<Record<number, Record<string, string | number>>>({});
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/grading/${id}`);
            setGrading(response.data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch grading details');
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (itemId: number, field: string, value: string) => {
        setEditedItems(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        try {
            const items = Object.entries(editedItems).map(([itemId, changes]) => ({
                item_id: Number(itemId),
                grade_a_qty: Number(changes.grade_a_qty ?? 0),
                grade_b_qty: Number(changes.grade_b_qty ?? 0),
                grade_c_qty: Number(changes.grade_c_qty ?? 0),
                qc_wastage_qty: Number(changes.qc_wastage_qty ?? 0),
                notes: changes.notes
            }));

            await apiClient.put(`/grading/${id}`, { items });
            fetchDetails();
            setEditedItems({});
            enqueueSnackbar('Changes saved successfully', { variant: 'success' });
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to save changes', { variant: 'error' });
        }
    };

    const handleComplete = async () => {
        try {
            await apiClient.post(`/grading/${id}/complete`);
            setCompleteDialogOpen(false);
            fetchDetails();
            enqueueSnackbar('Grading completed successfully', { variant: 'success' });
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to complete grading', { variant: 'error' });
        }
    };

    const handleGenerateGRN = async () => {
        try {
            const response = await apiClient.post('/grn-new/generate-from-grading', null, {
                params: { grading_id: id }
            });
            enqueueSnackbar('GRN Generated successfully', { variant: 'success' });
            navigate(`/inward/grn-new/${response.data.id}`);
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to generate GRN', { variant: 'error' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !grading) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || 'Grading not found'}</Alert>
            </Box>
        );
    }

    const isDraft = grading.status === 'draft';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/inward/grading')}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant="h4">Grading - {grading.batch_number}</Typography>
                    <Chip label={grading.status.toUpperCase()} color="primary" />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isDraft && Object.keys(editedItems).length > 0 && (
                        <Button variant="outlined" onClick={handleSave}>
                            Save Changes
                        </Button>
                    )}
                    {isDraft && (
                        <Button
                            variant="contained"
                            startIcon={<CompleteIcon />}
                            onClick={() => setCompleteDialogOpen(true)}
                        >
                            Complete
                        </Button>
                    )}
                    {grading.status === 'completed' && (
                        <Button
                            variant="contained"
                            startIcon={<GenerateGRNIcon />}
                            onClick={handleGenerateGRN}
                        >
                            Generate GRN
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Receiving #</Typography>
                                    <Typography variant="body1">{grading.receiving_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Batch #</Typography>
                                    <Typography variant="body1">{grading.batch_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">PO #</Typography>
                                    <Typography variant="body1">{grading.po_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Vendor / Farm</Typography>
                                    <Typography variant="body1">{grading.vendor_name}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Grading Date</Typography>
                                    <Typography variant="body1">
                                        {format(new Date(grading.grading_date), 'dd MMM yyyy')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Grade Assignment</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Source Qty</TableCell>
                                    <TableCell align="right">Grade A</TableCell>
                                    <TableCell align="right">Grade B</TableCell>
                                    <TableCell align="right">Grade C</TableCell>
                                    <TableCell align="right">QC Wastage</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grading.items.map((item) => {
                                    const edited = editedItems[item.item_id] || {};
                                    // Use edited value (string) if present, otherwise fallback to item value (number)
                                    const gradeA = edited.grade_a_qty ?? item.grade_a_qty;
                                    const gradeB = edited.grade_b_qty ?? item.grade_b_qty;
                                    const gradeC = edited.grade_c_qty ?? item.grade_c_qty;
                                    const wastage = edited.qc_wastage_qty ?? item.qc_wastage_qty;

                                    // For calculation, cast to Number
                                    const total = Number(gradeA) + Number(gradeB) + Number(gradeC) + Number(wastage);
                                    const isValid = total === Number(item.source_qty);

                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell>{item.item_name}</TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="bold">{item.source_qty}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {isDraft ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={gradeA}
                                                        onChange={(e) => handleItemChange(item.item_id, 'grade_a_qty', e.target.value)}
                                                        sx={{ width: 80 }}
                                                    />
                                                ) : (
                                                    gradeA
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isDraft ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={gradeB}
                                                        onChange={(e) => handleItemChange(item.item_id, 'grade_b_qty', e.target.value)}
                                                        sx={{ width: 80 }}
                                                    />
                                                ) : (
                                                    gradeB
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isDraft ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={gradeC}
                                                        onChange={(e) => handleItemChange(item.item_id, 'grade_c_qty', e.target.value)}
                                                        sx={{ width: 80 }}
                                                    />
                                                ) : (
                                                    gradeC
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isDraft ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={wastage}
                                                        onChange={(e) => handleItemChange(item.item_id, 'qc_wastage_qty', e.target.value)}
                                                        sx={{ width: 80 }}
                                                    />
                                                ) : (
                                                    wastage
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography color={isValid ? 'success.main' : 'error.main'}>
                                                    {total.toFixed(3)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Complete Dialog */}
            <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)}>
                <DialogTitle>Complete Grading?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will mark the grading as complete and log all QC wastage events.
                        Make sure all items are fully graded.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleComplete} variant="contained">
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GradingDetailPage;
